<script lang="ts">
  import OVIcon from "app/components/+shared/OVIcon/OVIcon.svelte";
  import Separator from "app/components/+shared/Separator/Separator.svelte";
  import { localStorageStore } from "app/stores/localStorage";
  import { createEventDispatcher, onMount } from "svelte";
  import { EMOJI_SIZE } from "./EmojiPicker.svelte";
  import { fade } from "svelte/transition";
  import { quintOut } from "svelte/easing";
  import { Expand } from "$components/+shared/OVIcon";
  import cx from "classnames";
  import type { Emoji } from "./types";

  let dispatch = createEventDispatcher();
  let recentEmojis = localStorageStore<Emoji[]>("recent-emojis", []);
  let skinIndex = localStorageStore<number>("emoji-skin-index", 0);
  let emojisList = new Set<Emoji>();

  let emojiPickerPreview: HTMLDivElement;
  let emojisListRef: HTMLDivElement;

  // a boolean to check if the emojis list is scrolled to the end
  let isScrolledToEnd = false;
  // a boolean to check if the emojis list has been scrolled to any point
  let isScrolled = true;

  function handleFade() {
    if (emojisListRef?.scrollLeft === 0) {
      isScrolled = true;
      return;
    }
    isScrolledToEnd =
      emojisListRef?.scrollWidth - emojisListRef?.scrollLeft <= emojisListRef?.clientWidth;
    isScrolled = false;
  }

  onMount(async () => {
    const emoji = await import("./emojisLabels");
    emojisList = new Set([...emoji.defaultEmojis, ...$recentEmojis.slice(0, 6)]);
    // this sets the fade mask on mount
    handleFade();
    // focus on the container so that the user can use the keyboard to navigate the emojis
    emojiPickerPreview?.focus();
  });
</script>

<div class="ov-focus flex items-center pr-2 after:rounded" bind:this={emojiPickerPreview}>
  <!-- mask-composite: destination-in allows for both masks (left and right in this case) to be applied-->
  <div
    class={cx(
      "no-scrollbar ov-focus flex h-full max-w-[196px] overflow-x-auto overflow-y-hidden p-2 after:rounded",
      {
        "[mask-composite:_destination-in] [mask-image:_linear-gradient(to_left,_transparent_2%,_black_16%),_linear-gradient(to_right,_transparent_1%,_black_10%)]":
          !isScrolled,
        "[mask-image:_linear-gradient(to_left,_transparent_2%,_black_16%)]": isScrolled,
        "[mask-image:_linear-gradient(to_right,_transparent_1%,_black_10%)]": isScrolledToEnd,
      }
    )}
    in:fade={{ duration: 400, delay: 0, easing: quintOut }}
    role="listbox"
    aria-label="recently used emojis"
    tabindex="0"
    on:scroll={handleFade}
    bind:this={emojisListRef}
  >
    {#each emojisList as emojiObj, index (`${emojiObj.id}-${index}`)}
      {@const emoji = emojiObj.skins[$skinIndex] ?? emojiObj.skins[0]}
      <button
        type="button"
        role="option"
        aria-selected="false"
        title={emojiObj.name}
        style={`width: ${EMOJI_SIZE}px; height: ${EMOJI_SIZE}px;`}
        class="ov-focus group flex shrink-0 items-center justify-center rounded p-1 transition-colors duration-75 after:rounded hover:cursor-pointer hover:bg-black/15 active:scale-[0.95] dark:hover:bg-white/15"
        on:click={() => dispatch("selection", emoji)}
      >
        <span class="duration-75 group-hover:scale-110 group-focus:scale-110">
          {emoji.native}
        </span>
      </button>
    {/each}
  </div>

  <Separator height="h-6" class="w-[1px]" />

  <button
    type="button"
    aria-label="see all emojis"
    style={`width: ${EMOJI_SIZE}px; height: ${EMOJI_SIZE}px;`}
    class="ov-focus ml-2 flex items-center justify-center rounded border-[0.5px] border-transparent p-1 transition-colors after:rounded hover:border-black/20 hover:bg-black/3 active:scale-[0.95] dark:hover:border-white/20 dark:hover:bg-white/3"
    on:click={() => dispatch("full-mode")}
  >
    <OVIcon icon={Expand} class="text-gray-500 dark:text-gray-400" />
  </button>
</div>
